import { useEffect, useMemo, useRef, useState } from 'react';
import { Eye, FileText, Search, Trash2, Upload, Users } from 'lucide-react';
import { toast } from 'sonner';
import { payrollService } from '../services/payrollService';

type PayrollRecord = {
  id: string;
  driver_id: string;
  file_path: string;
  file_name: string;
  created_at: string;
};

type PayrollDriver = {
  id: string;
  user_id: string;
  tc_number: string;
  city: string;
  cekici_plaka: string;
  dorse_plaka: string;
  start_date: string;
  termination_date: string;
  full_name: string;
  phone: string;
  user_status: string;
  payroll: PayrollRecord | null;
};

const TURKISH_MONTHS = [
  'Ocak',
  'Şubat',
  'Mart',
  'Nisan',
  'Mayıs',
  'Haziran',
  'Temmuz',
  'Ağustos',
  'Eylül',
  'Ekim',
  'Kasım',
  'Aralık',
];

const resolveFileUrl = (rawPath: string) => {
  if (!rawPath) return '';
  if (/^https?:\/\//i.test(rawPath)) return rawPath;
  const baseUrl = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');
  return `${baseUrl}${rawPath.startsWith('/') ? '' : '/'}${rawPath}`;
};

const normalizeText = (value?: string | null) => String(value || '').toLocaleLowerCase('tr-TR').trim();

const mapDriver = (driver: any): PayrollDriver => ({
  id: String(driver.id || ''),
  user_id: String(driver.user_id || ''),
  tc_number: driver.tc_number || '',
  city: driver.city || '',
  cekici_plaka: driver.cekici_plaka || '',
  dorse_plaka: driver.dorse_plaka || '',
  start_date: driver.start_date || '',
  termination_date: driver.termination_date || '',
  full_name: driver.full_name || '-',
  phone: driver.phone || '',
  user_status: driver.user_status || '',
  payroll: driver.payroll || null,
});

const yearOptions = (() => {
  const currentYear = new Date().getFullYear();
  const options: number[] = [];
  for (let year = currentYear + 1; year >= 2020; year -= 1) options.push(year);
  return options;
})();

export function DriverPayrolls() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [drivers, setDrivers] = useState<PayrollDriver[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [uploadTarget, setUploadTarget] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const loadPayrolls = async (targetYear: number, targetMonth: number) => {
    try {
      setLoading(true);
      const response: any = await payrollService.getPayrolls({ year: targetYear, month: targetMonth });
      const rawDrivers = Array.isArray(response?.data) ? response.data : [];
      setDrivers(rawDrivers.map(mapDriver).filter((driver: PayrollDriver) => driver.id));
    } catch (error) {
      console.error('Payroll list load failed:', error);
      toast.error('Maaş bordroları yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadPayrolls(year, month);
  }, [year, month]);

  const filteredDrivers = useMemo(() => {
    const query = normalizeText(searchTerm);
    if (!query) return drivers;
    return drivers.filter((driver) =>
      normalizeText(`${driver.full_name} ${driver.phone} ${driver.tc_number} ${driver.cekici_plaka} ${driver.dorse_plaka} ${driver.city}`).includes(query)
    );
  }, [drivers, searchTerm]);

  const summary = useMemo(() => ({
    total: filteredDrivers.length,
    uploaded: filteredDrivers.filter((driver) => Boolean(driver.payroll)).length,
    missing: filteredDrivers.filter((driver) => !driver.payroll).length,
  }), [filteredDrivers]);

  const openPayroll = (driver: PayrollDriver) => {
    const url = resolveFileUrl(driver.payroll?.file_path || '');
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleUploadClick = (driverId: string) => {
    setUploadTarget(driverId);
    fileInputRef.current?.click();
  };

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const targetDriverId = uploadTarget;
    event.target.value = '';
    if (!file || !targetDriverId) return;

    if (file.type !== 'application/pdf' && !/\.pdf$/i.test(file.name)) {
      toast.error('Sadece PDF dosyası yüklenebilir');
      return;
    }

    try {
      setUploading(true);
      const payload = new FormData();
      payload.append('file', file);
      payload.append('driverId', targetDriverId);
      payload.append('year', String(year));
      payload.append('month', String(month));
      await payrollService.uploadPayroll(payload);
      await loadPayrolls(year, month);
      toast.success('Maaş bordrosu yüklendi');
    } catch (error) {
      console.error('Payroll upload failed:', error);
      toast.error(error instanceof Error ? error.message : 'Maaş bordrosu yüklenemedi');
    } finally {
      setUploading(false);
      setUploadTarget(null);
    }
  };

  const handleDelete = async (driver: PayrollDriver) => {
    if (!driver.payroll) return;
    if (!window.confirm(`${driver.full_name} için ${TURKISH_MONTHS[month - 1]} ${year} bordrosu silinecek. Onaylıyor musunuz?`)) return;
    try {
      await payrollService.deletePayroll(driver.payroll.id);
      await loadPayrolls(year, month);
      toast.success('Maaş bordrosu silindi');
    } catch (error) {
      console.error('Payroll delete failed:', error);
      toast.error('Maaş bordrosu silinemedi');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Maaş Bordroları</h1>
          <p className="mt-1 text-gray-500">Özmal sürücülerin seçili döneme ait maaş bordrolarını yükleyin ve görüntüleyin</p>
        </div>
        <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:flex-row sm:items-end">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Ay</label>
            <select value={month} onChange={(e) => setMonth(Number(e.target.value))} className="h-10 rounded-xl border border-slate-300 px-3 text-sm">
              {TURKISH_MONTHS.map((label, index) => (
                <option key={label} value={index + 1}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Yıl</label>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="h-10 rounded-xl border border-slate-300 px-3 text-sm">
              {yearOptions.map((optionYear) => (
                <option key={optionYear} value={optionYear}>{optionYear}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        onChange={handleUpload}
        disabled={uploading}
      />

      <div className="grid gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500">Sürücü Sayısı</div>
          <div className="mt-1 text-2xl font-bold text-slate-900">{summary.total}</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500">Bordrosu Yüklü</div>
          <div className="mt-1 text-2xl font-bold text-emerald-700">{summary.uploaded}</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500">Eksik</div>
          <div className="mt-1 text-2xl font-bold text-rose-700">{summary.missing}</div>
        </div>
      </div>

      <div className="relative max-w-xl">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Sürücü, telefon, TC veya plaka ara"
          className="h-11 w-full rounded-xl border border-gray-300 py-2 pl-9 pr-3 text-sm"
        />
      </div>

      <div className="overflow-hidden rounded-2xl border border-black bg-white shadow-sm">
        <div className="max-h-[70vh] overflow-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="sticky top-0 z-20 min-w-[280px] border-b border-r border-black bg-gray-50 px-4 py-3 text-left font-medium text-gray-600">Sürücü</th>
                <th className="sticky top-0 z-20 min-w-[140px] border-b border-r border-black bg-gray-50 px-4 py-3 text-left font-medium text-gray-600">Durum</th>
                <th className="sticky top-0 z-20 min-w-[260px] border-b border-black bg-gray-50 px-4 py-3 text-left font-medium text-gray-600">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={3} className="px-4 py-10 text-center text-gray-500">Yükleniyor...</td>
                </tr>
              ) : filteredDrivers.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-4 py-10 text-center text-gray-500">Kayıt bulunamadı</td>
                </tr>
              ) : filteredDrivers.map((driver) => (
                <tr key={`payroll-${driver.id}`} className="border-t border-black">
                  <td className="border-r border-black bg-white px-4 py-3 align-top">
                    <div className="flex items-start gap-3">
                      <div className="rounded-xl bg-violet-100 p-2 text-violet-700">
                        <Users className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-gray-900">{driver.full_name}</div>
                        <div className="mt-1 text-xs text-gray-500">{driver.phone || '-'} {driver.tc_number ? `• ${driver.tc_number}` : ''}</div>
                        <div className="mt-1 text-xs text-gray-400">{driver.cekici_plaka || '-'} {driver.dorse_plaka ? `/ ${driver.dorse_plaka}` : ''}</div>
                        {driver.city ? <div className="mt-1 text-xs text-gray-400">{driver.city}</div> : null}
                      </div>
                    </div>
                  </td>
                  <td className="border-r border-black px-4 py-3 align-top">
                    {driver.payroll ? (
                      <div>
                        <span className="inline-flex rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">Yüklendi</span>
                        <div className="mt-1.5 text-[11px] text-slate-500">
                          {driver.payroll.created_at ? new Date(driver.payroll.created_at).toLocaleString('tr-TR') : ''}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <span className="inline-flex rounded-full bg-rose-50 px-2.5 py-1 text-xs font-bold text-rose-700">Eksik</span>
                        <div className="mt-1.5 text-[11px] text-rose-500">Bu döneme ait bordro yüklenmemiş.</div>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div className="flex flex-wrap gap-2">
                      {driver.payroll ? (
                        <>
                          <button
                            type="button"
                            onClick={() => openPayroll(driver)}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100"
                          >
                            <Eye className="h-3.5 w-3.5" /> Görüntüle
                          </button>
                          <button
                            type="button"
                            onClick={() => handleUploadClick(driver.id)}
                            disabled={uploading}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-60"
                          >
                            <Upload className="h-3.5 w-3.5" /> Değiştir
                          </button>
                          <button
                            type="button"
                            onClick={() => void handleDelete(driver)}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100"
                          >
                            <Trash2 className="h-3.5 w-3.5" /> Sil
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleUploadClick(driver.id)}
                          disabled={uploading}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-rose-100 px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-200 disabled:opacity-60"
                        >
                          <Upload className="h-3.5 w-3.5" /> {uploading && uploadTarget === driver.id ? 'Yükleniyor...' : 'Yükle'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
